@if (visible().length) {
  <div class="attachments" data-cy="email-attachments">
    <span class="attachments__heading">Files:</span>

    <div class="attachments__list">
      @for (attachment of visible(); track attachment.getId()) {
        <div
          class="attachments__card"
          [class.attachments__card--child]="attachment.isChild()"
          [class.attachments__card--gone]="attachment.isGone()"
          [attr.data-cy]="'email-attachment-' + attachment.getId()"
        >
          <div class="attachments__head">
            <i
              class="pi attachments__icon"
              [ngClass]="iconClass(attachment)"
              aria-hidden="true"
            ></i>

            <span class="attachments__name">{{
              attachment.getFilename()
            }}</span>

            <span class="attachments__actions">
              <!-- Preview and download are removed rather than disabled for an infected or expired
                   file: there is nothing to offer, and a greyed-out button invites a retry. -->
              @if (attachment.canBePreviewed()) {
                <button
                  pButton
                  type="button"
                  size="small"
                  text
                  severity="secondary"
                  pTooltip="Preview"
                  tooltipPosition="top"
                  [attr.aria-label]="'Preview ' + attachment.getFilename()"
                  (click)="open(attachment, false)"
                >
                  <i class="pi pi-eye" aria-hidden="true"></i>
                </button>
              }

              @if (!attachment.isGone() && !attachment.isInfected()) {
                <button
                  pButton
                  type="button"
                  size="small"
                  text
                  severity="secondary"
                  pTooltip="Download"
                  tooltipPosition="top"
                  [attr.aria-label]="'Download ' + attachment.getFilename()"
                  (click)="open(attachment, true)"
                >
                  <i class="pi pi-download" aria-hidden="true"></i>
                </button>
              }

              @if (
                canDelete() && !attachment.isGone() && !isConfirming(attachment)
              ) {
                <button
                  pButton
                  type="button"
                  size="small"
                  text
                  severity="danger"
                  pTooltip="Delete"
                  tooltipPosition="top"
                  [attr.aria-label]="'Delete ' + attachment.getFilename()"
                  [attr.data-cy]="
                    'email-attachment-delete-' + attachment.getId()
                  "
                  (click)="remove(attachment)"
                >
                  <i class="pi pi-trash" aria-hidden="true"></i>
                </button>
              }
            </span>
          </div>

          <div class="attachments__meta">
            <span class="attachments__badges">
              @if (attachment.isDeleted()) {
                <span class="attachments__badge attachments__badge--removed"
                  >removed</span
                >
              }

              @if (attachment.wasUsedByAi()) {
                <button
                  type="button"
                  class="attachments__badge attachments__badge--used"
                  [attr.aria-expanded]="isExpanded(attachment)"
                  [pTooltip]="
                    isUnattributed(attachment)
                      ? 'The AI read this file but did not say which values came from it'
                      : 'Show what this file provided'
                  "
                  tooltipPosition="top"
                  (click)="toggleFields(attachment)"
                >
                  used by AI
                  @if (!isUnattributed(attachment)) {
                    <i
                      class="pi attachments__badge-caret"
                      [class.pi-chevron-down]="!isExpanded(attachment)"
                      [class.pi-chevron-up]="isExpanded(attachment)"
                      aria-hidden="true"
                    ></i>
                  }
                </button>
              }

              @if (attachment.isProcessing()) {
                <span class="attachments__badge attachments__badge--processing"
                  >processing</span
                >
              }

              @if (attachment.isInfected()) {
                <span class="attachments__badge attachments__badge--infected"
                  >infected</span
                >
              }

              @if (attachment.isExpired()) {
                <span class="attachments__badge">no longer available</span>
              }

              @if (
                !attachment.wasUsedByAi() &&
                !attachment.isProcessing() &&
                !attachment.isInfected() &&
                attachment.getClassificationReason()
              ) {
                <span
                  class="attachments__badge"
                  [pTooltip]="attachment.getClassificationReason()"
                  tooltipPosition="top"
                  >not analyzed</span
                >
              }

              @if (attachment.wasNotScanned()) {
                <span
                  class="attachments__badge"
                  pTooltip="No virus scanner is configured, so this file was not checked."
                  tooltipPosition="top"
                  >not scanned</span
                >
              }
            </span>

            <span class="attachments__size">{{ sizeLabel(attachment) }}</span>
          </div>

          @if (isExpanded(attachment) && !isUnattributed(attachment)) {
            <div class="attachments__fields">
              <span class="attachments__fields-heading">Provided</span>
              <ul class="attachments__fields-list">
                @for (
                  field of attachment.getContributedFieldLabels();
                  track $index
                ) {
                  <li>{{ field }}</li>
                }
              </ul>
            </div>
          }

          @if (attachment.isDeleted()) {
            <span class="attachments__note">
              Removed
              @if (attachment.getDeleteReason()) {
                — {{ attachment.getDeleteReason() }}
              }
            </span>
          }

          @if (attachment.isExpired()) {
            <span class="attachments__note"
              >File no longer available — its retention period has passed.</span
            >
          }

          @if (isConfirming(attachment)) {
            <div class="attachments__confirm">
              <span class="attachments__confirm-alert">
                <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                This cannot be undone.
              </span>
              <span class="attachments__confirm-question"
                >Why are you removing this file?</span
              >
              <div class="attachments__confirm-reasons">
                @for (reason of reasons(); track reason.value) {
                  <label class="attachments__confirm-reason">
                    <input
                      type="radio"
                      name="delete-reason"
                      [value]="reason.value"
                      [checked]="chosenReason() === reason.value"
                      (change)="chooseReason(reason.value)"
                    />
                    {{ reason.label }}
                  </label>
                }
              </div>
              <div class="attachments__confirm-actions">
                <button
                  pButton
                  type="button"
                  size="small"
                  severity="secondary"
                  [outlined]="true"
                  (click)="cancelRemove()"
                >
                  Cancel
                </button>
                <button
                  pButton
                  type="button"
                  size="small"
                  severity="danger"
                  [outlined]="true"
                  [disabled]="!chosenReason()"
                  [attr.data-cy]="
                    'email-attachment-confirm-' + attachment.getId()
                  "
                  (click)="confirmRemove(attachment)"
                >
                  Remove file
                </button>
              </div>
            </div>
          }
        </div>
      }
    </div>
  </div>
}
